// Het trendpaneel in de nieuwe weergave, op de manier van het Skills-paneel
// (owner 14-10, Clipy yrweosl2meup, T3–T5):
//  · bovenaan alleen de labels als pil; de reden staat bij hover;
//  · de korte samenvatting open; het verhaal (bronnen ná elk inzicht) en "Wat dit
//    betekent voor het onderwijs" als twee aparte uitklapregels (T4, huisregel 19);
//  · de drie scores als meters;
//  · uitklapregels die op hun plek openklappen, ook regels binnen regels (T5):
//    waarom deze cijfers · bewijs per kwartaal (per kwartaal de bronnen) ·
//    Brainport · gevraagde skills (per skill de koppeling) · samenhang (per
//    trend de samenvatting) · regelingen (RegelingRegel; klik = een kaart als
//    laag bovenop) · alle bronnen ("en nog N in de kennisbank");
//  · een bron openen = een tweede paneel bovenop (huisregel 4).
// Inhoud uit components/TrendDetail.tsx; die blijft ongemoeid.
// Samenvoeglaag: draagt de trend `samenvoeging`, dan staat bovenaan "Hoort bij".

import { BarChart3, BookOpen, BookText, CalendarRange, GitFork, GraduationCap, Landmark, MapPin, Puzzle } from "lucide-react";
import { Fragment, type ReactNode, useState } from "react";

import { SECTORBREED, sectorVoorLabels } from "../../components/RegelingenStrook";
import type { FundingVoorResponse, Trend } from "../../contracts";
import type { SkillRef } from "../../contracts/api/trends";
import { useFundingVoor } from "../../lib/funding";
import { naarTab } from "../../lib/naarTab";
import { AFGELEID_UITLEG, TREDE_LABEL } from "../../lib/schermen";
import {
  AiIcoon,
  BronnenLijst,
  BronVermelding,
  Knop,
  type Label,
  Meters,
  Paneel,
  type PaneelBreedte,
  PaneelKnoppen,
  Pil,
  RegelingRegel,
  SkillRegel,
  TrendRegel,
  type Toon,
  Uitklapregel,
  vars,
  Zweef,
} from "../../stijl";
import { alsBron, type Bewijsregel, datum, kwartaal, labelVan, meervoud, metaVan, niveauWoord, opDatum, titel } from "./data";
import { useOpenKaart } from "../kaartlaag";
import { samenvoegingVan, samenvoegToon } from "./samenvoeging";

type OpenBron = (b: Bewijsregel) => void;

// ── Tekst met bronnen ná het inzicht ───────────────────────────────────────

/** Knipt een tekst met {n}-markers in stukken: tekst, dan de bronnen die
 *  erachter staan (opeenvolgende markers vormen één vermelding). */
export function stukken(tekst: string, bewijs: readonly Bewijsregel[]): { tekst: string; bronnen: Bewijsregel[] }[] {
  const uit: { tekst: string; bronnen: Bewijsregel[] }[] = [];
  for (const deel of tekst.split(/(\{\d+\})/g)) {
    const m = deel.match(/^\{(\d+)\}$/);
    if (m) {
      const b = bewijs[Number(m[1])];
      if (!b) continue; // marker buiten bereik: liever niets dan een verkeerde bron
      const laatste = uit[uit.length - 1];
      if (laatste && !laatste.bronnen.some((x) => x.id === b.id)) laatste.bronnen.push(b);
      else if (!laatste) uit.push({ tekst: "", bronnen: [b] });
      continue;
    }
    if (!deel) continue;
    const laatste = uit[uit.length - 1];
    // Alleen spaties tussen twee markers: dezelfde vermelding.
    if (laatste && laatste.bronnen.length && !deel.trim()) continue;
    uit.push({ tekst: deel, bronnen: [] });
  }
  return uit;
}

function MetBronnen({ tekst, bewijs, onBron }: { tekst: string; bewijs: readonly Bewijsregel[]; onBron: OpenBron }) {
  return (
    <>
      {stukken(tekst, bewijs).map((s, i) => (
        <Fragment key={i}>
          {s.tekst}
          {s.bronnen.length > 0 && <BronVermelding bronnen={s.bronnen.map((b) => alsBron(b, onBron))} />}
        </Fragment>
      ))}
    </>
  );
}

/** Een tekst in alinea's (gescheiden door een witregel). */
function Verhaal({ tekst, bewijs, onBron }: { tekst: string; bewijs: readonly Bewijsregel[]; onBron: OpenBron }) {
  const alineas = tekst.split(/\n\s*\n/).filter((a) => a.trim());
  return (
    <div className="t-verhaal">
      {alineas.map((a, i) => (
        <div key={i} className="t-verhaal__alinea">
          <MetBronnen tekst={a.trim()} bewijs={bewijs} onBron={onBron} />
        </div>
      ))}
    </div>
  );
}

// ── Kleine helpers ─────────────────────────────────────────────────────────

const SCORES: { k: "trending" | "impact" | "urgency"; label: string; uitleg: string }[] = [
  { k: "trending", label: "Trending", uitleg: "Hoe sterk het signaal nu is: veel en recent bewijs." },
  { k: "impact", label: "Impact", uitleg: "Hoe ingrijpend de verandering is voor het werk." },
  { k: "urgency", label: "Urgentie", uitleg: "Hoe snel het speelt: wanneer moet je erop inspelen." },
];
const woord = (w: number) => (w >= 8 ? "hoog" : w >= 6 ? "gemiddeld" : "laag");

/** Een label als pil met de reden bij hover (zoals het Skills-paneel). */
function LabelPil({ label }: { label: Label }) {
  return (
    <Zweef tekst={label.uitleg}>
      <span tabIndex={0} className="t-labelpil">
        <Pil toon={label.toon}>{label.tekst}</Pil>
      </span>
    </Zweef>
  );
}

// ── Blokken ────────────────────────────────────────────────────────────────

function HoortBij({ trend, onKopTrend, onOntwikkeling }: { trend: Trend; onKopTrend?: (id: string) => void; onOntwikkeling?: (sleutel: string) => void }) {
  const sv = samenvoegingVan(trend);
  if (!sv) return null;
  const hb = sv.hoortBij;
  if (sv.soort === "los")
    return (
      <div className="t-hoortbij" data-testid="hoort-bij">
        <span className="s-meta">Los signaal</span>
        {sv.reden && <span>{sv.reden}</span>}
      </div>
    );
  if (!hb) return null;
  return (
    <div className="t-hoortbij" data-testid="hoort-bij">
      <span className="s-meta">Hoort bij</span>
      <span className="t-hoortbij__titel">
        {onOntwikkeling ? (
          <Knop variant="link" onClick={() => onOntwikkeling(hb.sleutel)}>
            {hb.titel}
          </Knop>
        ) : hb.kopTrendId && onKopTrend && hb.kopTrendId !== trend.id ? (
          <Knop variant="link" onClick={() => onKopTrend(hb.kopTrendId!)}>
            {hb.titel}
          </Knop>
        ) : (
          <strong>{hb.titel}</strong>
        )}
        {hb.label.tekst && <LabelPil label={{ tekst: hb.label.tekst, toon: samenvoegToon(hb.label.soort), uitleg: hb.label.regel ?? hb.label.uitleg }} />}
        <span className="s-meta">{meervoud(hb.nTrends, "trend", "trends")}</span>
      </span>
      {hb.werkzin && <span>{hb.werkzin}</span>}
    </div>
  );
}

/** Per kwartaal een regel met een balkje; open = de bronnen van dat kwartaal. */
function PerKwartaal({ bewijs, onBron }: { bewijs: readonly Bewijsregel[]; onBron: OpenBron }) {
  const [open, setOpen] = useState<string | null>(null);
  const per = new Map<string, Bewijsregel[]>();
  for (const b of bewijs) per.set(kwartaal(b.date), [...(per.get(kwartaal(b.date)) ?? []), b]);
  const max = Math.max(1, ...[...per.values()].map((l) => l.length));
  return (
    <div className="t-kwartalen">
      {[...per.entries()].map(([q, lijst]) => (
        <Uitklapregel
          key={q}
          klein
          open={open === q}
          onOpen={(o) => setOpen(o ? q : null)}
          kop={
            <span className="t-kwartaal">
              <span className="t-kwartaal__q">{q}</span>
              <span className="t-kwartaal__balk" aria-hidden>
                <span style={vars({ "--w": `${Math.round((lijst.length / max) * 100)}%` })} />
              </span>
            </span>
          }
          rechts={meervoud(lijst.length, "bron", "bronnen")}
        >
          <BronnenLijst bronnen={lijst.map((b) => alsBron(b, onBron))} />
        </Uitklapregel>
      ))}
    </div>
  );
}

/** T4: wat dit betekent voor het onderwijs, als eigen blok. */
function Onderwijs({ trend, bewijs, onBron }: { trend: Trend; bewijs: readonly Bewijsregel[]; onBron: OpenBron }) {
  const e = trend.education;
  if (!e?.now && !e?.future) return null;
  const afgeleid = e.herkomst === "afgeleid";
  return (
    <div className="t-blokken" aria-label="Wat dit betekent voor het onderwijs">
      {afgeleid && (
        <div className="t-labels">
          <LabelPil label={{ tekst: e.herkomstLabel || "afgeleid door het platform", toon: "oranje", uitleg: AFGELEID_UITLEG }} />
        </div>
      )}
      {e.now && (
        <div className="t-sub">
          <h4 className="t-sub__kop">Nu</h4>
          <Verhaal tekst={e.now} bewijs={bewijs} onBron={onBron} />
        </div>
      )}
      {e.future && (
        <div className="t-sub">
          <h4 className="t-sub__kop">Over twee jaar</h4>
          <Verhaal tekst={e.future} bewijs={bewijs} onBron={onBron} />
        </div>
      )}
    </div>
  );
}

/** Gevraagde skills: per skill een regel; een klik opent de skill als kaart
 *  bovenop het paneel (kaart in kaart, huisregel 4). */
function Skills({ trend, onSkill }: { trend: Trend; onSkill: (s: SkillRef) => void }) {
  if (!trend.skills.length) return <p className="s-meta">Nog geen vaardigheid gekoppeld: de brug vond onder deze trend geen bewijs dat een skill aanwijst.</p>;
  return (
    <div className="t-regels">
      {trend.skills.map((s) => {
        const zwak = s.relation === "raakt";
        const trede = s.evidenceRung ? TREDE_LABEL[s.evidenceRung] : null;
        return <SkillRegel key={s.id} titel={s.name} meta={[zwak ? "raakt aan" : "vraagt om", s.type, trede].filter(Boolean).join(" · ")} onOpen={() => onSkill(s)} />;
      })}
    </div>
  );
}

/** Samenhang: de hoofdtrend en de onderbouwende trends; een klik opent de
 *  trend als kaart bovenop het paneel (huisregel 4). */
function Samenhang({ trend, ouder, kinderen, onTrend }: { trend: Trend; ouder: Trend | null; kinderen: Trend[]; onTrend: (t: Trend) => void }) {
  return (
    <div className="t-regels">
      {ouder && <TrendRegel titel={titel(ouder)} meta={`hoofdtrend · ${metaVan(ouder)}`} label={labelVan(ouder, { inRegel: true })} onOpen={() => onTrend(ouder)} />}
      {kinderen.map((c) => (
        <TrendRegel key={c.id} titel={titel(c)} meta={`onderbouwt · ${metaVan(c)}`} label={labelVan(c, { inRegel: true })} onOpen={() => onTrend(c)} />
      ))}
      {!kinderen.length && trend.childIds.length > 0 && (
        <p className="s-meta">{meervoud(trend.childIds.length, "onderliggende trend", "onderliggende trends")}; open deze trend vanuit de lijst om ze te zien.</p>
      )}
    </div>
  );
}

type RegelingItem = FundingVoorResponse["items"][number];
const statusToon = (it: RegelingItem): Toon => (it.pasmaat.handelNu ? "oranje" : it.regeling.status === "open" ? "groen" : it.regeling.status === "aangekondigd" ? "blauw" : "neutraal");
const STATUS_WOORD: Record<string, string> = { open: "open", aangekondigd: "aangekondigd", gesloten: "gesloten", onbekend: "status onbekend" };
const venster = (it: RegelingItem): string => {
  const v = it.regeling.aanvraagVenster;
  return v?.eind ? `sluit ${datum(v.eind)}` : v?.start ? `opent ${datum(v.start)}` : v?.soort === "doorlopend" ? "doorlopend" : "datum onbekend";
};

function Regelingen({ trend, onKies }: { trend: Trend; onKies: (it: RegelingItem) => void }) {
  const { sleutel, eenduidig } = sectorVoorLabels(trend.sectors);
  const r = useFundingVoor({ sector: sleutel, limiet: 3 });
  if (r.loading && !r.data) return <p className="s-meta">Regelingen laden…</p>;
  if (r.error) return <p className="s-meta">De regelingen konden niet worden opgehaald: {r.error}</p>;
  const items = r.data?.items ?? [];
  if (!items.length) return <p className="s-meta">Er staat nu geen regeling open of aangekondigd voor {eenduidig ? trend.sectors[0] : "deze sectoren"}.</p>;
  return (
    <div className="t-regels">
      {items.map((it) => (
        <RegelingRegel
          key={it.regeling.id}
          naam={it.regeling.naam}
          sub={[it.regeling.verstrekker, venster(it)].filter(Boolean).join(" · ")}
          status={it.pasmaat.handelNu ? "handel nu" : STATUS_WOORD[it.regeling.status]}
          toon={statusToon(it)}
          onOpen={() => onKies(it)}
        />
      ))}
      <p className="s-meta">
        {r.data?.label ?? "Kansrijk"}: een suggestie voor {eenduidig ? `de sector ${trend.sectors[0]}` : "alle sectoren"}, geen oordeel over jouw situatie.{" "}
        {(r.data?.totaal ?? 0) > items.length && (
          <Knop variant="link" onClick={() => naarTab({ soort: "tab", tab: "funding", filter: { sector: [sleutel === SECTORBREED ? SECTORBREED : sleutel] } })}>
            Alle {r.data!.totaal} op de Funding-tab
          </Knop>
        )}
      </p>
    </div>
  );
}

// ── Het paneel ─────────────────────────────────────────────────────────────
// Eén inhoud voor twee plekken (huisregel 4): het paneel op de Trends-pagina
// en de laag "kaart in kaart" (trends/kaart.tsx). Kop, inhoud en voet komen
// uit dezelfde functies, zodat de laag precies de originele kaart is.

type Regel = "verhaal" | "onderwijs" | "cijfers" | "kwartaal" | "brainport" | "skills" | "samenhang" | "regelingen" | "bronnen";

/** De inhoud van de trendkaart. Een skill, trend, regeling of bron daarin
 *  opent zijn eigen originele kaart als laag erbovenop (useOpenKaart). */
export function TrendInhoud({
  trend,
  ouder = null,
  kinderen = [],
  onKopTrend,
  onOntwikkeling,
}: {
  trend: Trend;
  ouder?: Trend | null;
  kinderen?: Trend[];
  onKopTrend?: (id: string) => void;
  onOntwikkeling?: (sleutel: string) => void;
}) {
  const openKaart = useOpenKaart();
  const [open, setOpen] = useState<Regel | null>(null);
  const regel = (k: Regel) => ({ open: open === k, onOpen: (o: boolean) => setOpen(o ? k : null) });
  const bewijs = opDatum(trend.evidence ?? []);
  const label = labelVan(trend);
  const sv = samenvoegingVan(trend);
  const nieuwste = [...bewijs].reverse().find((b) => b.date);
  const metStructuur = !sv && (ouder || kinderen.length > 0 || trend.childIds.length > 0);
  // Een bron opent alleen als hij in de kennisbank staat (sourceId); anders
  // blijft het bij de bronkaart met de link naar het origineel.
  const onBron: OpenBron = (b) => b.sourceId && openKaart({ soort: "bron", id: b.sourceId, titel: b.title || b.publisher });

  return (
    <div className="t-paneel">
      <div className="t-paneel__kop">
        <span className="s-meta">{[niveauWoord(trend.level), trend.category ?? trend.categoryProposed].filter(Boolean).join(" · ")}</span>
        <h2 className="t-paneel__titel">{titel(trend)}</h2>
        {label && (
          <div className="t-labels" aria-label="Labels">
            <LabelPil label={label} />
          </div>
        )}
      </div>

      <HoortBij trend={trend} onKopTrend={onKopTrend} onOntwikkeling={onOntwikkeling} />

      {trend.tldr && <p className="t-paneel__lead">{trend.tldr}</p>}

      <section aria-label="In één oogopslag">
        <h3 className="t-blokkop">In één oogopslag</h3>
        <Meters regels={SCORES.map((s) => ({ label: s.label, waarde: trend.scores?.[s.k] ?? 0, woord: woord(trend.scores?.[s.k] ?? 0) }))} />
      </section>

      {/* Rustig eerst (huisregel 19): het verhaal en wat het voor het onderwijs
          betekent zijn twee aparte regels (T4) die op hun plek openklappen. */}
      <div className="t-regelgroep" role="group" aria-label="Meer over deze trend">
        {trend.description && (
          <Uitklapregel icoon={BookText} kop="Het verhaal" rechts={bewijs.length ? meervoud(new Set(bewijs.map((b) => b.publisher)).size, "uitgever", "uitgevers") : undefined} {...regel("verhaal")}>
            <Verhaal tekst={trend.description} bewijs={bewijs} onBron={onBron} />
          </Uitklapregel>
        )}
        {(trend.education?.now || trend.education?.future) && (
          <Uitklapregel icoon={GraduationCap} kop="Wat dit betekent voor het onderwijs" {...regel("onderwijs")}>
            <Onderwijs trend={trend} bewijs={bewijs} onBron={onBron} />
          </Uitklapregel>
        )}
        <Uitklapregel icoon={BarChart3} kop="Waarom deze cijfers" {...regel("cijfers")}>
          <div className="t-blokken">
            {SCORES.map((s) => (
              <section key={s.k} className="t-sub">
                <h4 className="t-sub__kop">
                  <Zweef tekst={s.uitleg}>
                    <span tabIndex={0}>{s.label}</span>
                  </Zweef>{" "}
                  <span className="s-meta">{(trend.scores?.[s.k] ?? 0).toFixed(1).replace(".", ",")}</span>
                </h4>
                {trend.scoreRationale?.[s.k] ? <Verhaal tekst={trend.scoreRationale[s.k]} bewijs={bewijs} onBron={onBron} /> : <p className="s-meta">Geen motivering vastgelegd.</p>}
              </section>
            ))}
            {(trend.uitlichtBadges ?? []).length > 0 && <p className="s-meta">Uitgelicht omdat: {trend.uitlichtBadges!.join(" · ")}</p>}
          </div>
        </Uitklapregel>
        <Uitklapregel icoon={CalendarRange} kop="Bewijs per kwartaal" rechts={nieuwste ? `nieuwste ${datum(nieuwste.date)}` : undefined} {...regel("kwartaal")}>
          {bewijs.length ? <PerKwartaal bewijs={bewijs} onBron={onBron} /> : <p className="s-meta">Geen bewijsregels.</p>}
        </Uitklapregel>
        {trend.brainport && (
          <Uitklapregel icoon={MapPin} kop="Brainport-regio" {...regel("brainport")}>
            <div className="t-blokken">
              <div className="t-labels">
                <LabelPil
                  label={
                    (trend.brainport.sources ?? []).length
                      ? { tekst: "regionale bron aanwezig", toon: "oranje", uitleg: "Onder deze alinea ligt bewijs van een regionale uitgever." }
                      : { tekst: "interpretatie, geen regionale bron", toon: "oranje", uitleg: "Een interpretatie van landelijk en Europees bewijs; er is geen regionale bron die dit voor Brainport meet." }
                  }
                />
              </div>
              <p>{trend.brainport.comparison}</p>
              <p className="s-meta">{trend.brainport.context}</p>
            </div>
          </Uitklapregel>
        )}
        <Uitklapregel icoon={Puzzle} kop="Gevraagde skills" rechts={trend.skills.length} {...regel("skills")}>
          <Skills trend={trend} onSkill={(sk) => openKaart({ soort: "skill", id: sk.id, titel: sk.name })} />
        </Uitklapregel>
        {metStructuur && (
          <Uitklapregel icoon={GitFork} kop="Samenhang met andere trends" rechts={kinderen.length || trend.childIds.length || undefined} {...regel("samenhang")}>
            <Samenhang trend={trend} ouder={ouder} kinderen={kinderen} onTrend={(t) => openKaart({ soort: "trend", id: t.id, titel: titel(t) })} />
          </Uitklapregel>
        )}
        <Uitklapregel icoon={Landmark} kop="Regelingen" {...regel("regelingen")}>
          <Regelingen trend={trend} onKies={(it) => openKaart({ soort: "regeling", id: it.regeling.id, titel: it.regeling.naam })} />
        </Uitklapregel>
        <Uitklapregel icoon={BookOpen} kop="Alle bronnen" rechts={trend.sourceCount} {...regel("bronnen")}>
          <BronnenLijst bronnen={bewijs.map((b) => alsBron(b, onBron))} totaal={Math.max(trend.sourceCount, bewijs.length)} />
        </Uitklapregel>
      </div>
    </div>
  );
}

/** De kop van de trendkaart (boven in het paneel en in de laag). */
export function trendKop(trend: Trend): ReactNode {
  return (
    <span className="s-meta t-paneel__soort">
      {trend.laag === "context" ? "Onderbouwende trend" : "Trend"} · {meervoud(trend.sourceCount, "bron", "bronnen")}
    </span>
  );
}

/** De voet van de trendkaart: vraag de bronnen (AI-icoon, huisregel 25). */
export function trendVoet(trend: Trend): ReactNode {
  return (
    <Knop variant="primair" icoon={AiIcoon} onClick={() => naarTab({ soort: "chat", vraag: `Wat zeggen de bronnen over de trend "${titel(trend)}"?` })}>
      Vraag de bronnen
    </Knop>
  );
}

/** Het paneel op de Trends-pagina. De laag gebruikt dezelfde drie delen. */
export function TrendPaneel({
  trend,
  ouder,
  kinderen,
  onSluit,
  onKopTrend,
  onOntwikkeling,
  paneel,
}: {
  trend: Trend | null;
  ouder: Trend | null;
  kinderen: Trend[];
  onSluit: () => void;
  /** Blijft voor de aanroepers; een trend in het paneel opent zelf als laag. */
  onTrend?: (t: Trend) => void;
  onKopTrend?: (id: string) => void;
  /** Alleen in de proef met ontwikkelingen: "Hoort bij" opent de ontwikkeling. */
  onOntwikkeling?: (sleutel: string) => void;
  paneel: PaneelBreedte;
}): ReactNode {
  return (
    <Paneel
      open={!!trend && !paneel.verborgen}
      onSluit={onSluit}
      greep={paneel.greep}
      vol={paneel.vol}
      label={trend ? titel(trend) : "Trend"}
      kop={trend && trendKop(trend)}
      kopActies={<PaneelKnoppen p={paneel} />}
      voet={trend && trendVoet(trend)}
    >
      {trend && <TrendInhoud key={trend.id} trend={trend} ouder={ouder} kinderen={kinderen} onKopTrend={onKopTrend} onOntwikkeling={onOntwikkeling} />}
    </Paneel>
  );
}
